import { apiClient } from '@app/api';
import { FormActionsMui } from '@app/components/form/actions/FormActionsMui';
import { SocialExportContext } from '@app/modules/social-export/components/dialogs/social-export-context';
import { SocialPlatform } from '@app/modules/social-export/domain/platforms';
import { useSocialExportFactory } from '@app/modules/social-export/hooks/useSocialExportFactory';
import { QueryView } from '@mi-front/ui';
import CloseIcon from '@mui/icons-material/Close';
import UploadIcon from '@mui/icons-material/Upload';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { FormattedMessage, useIntl } from 'react-intl';
import { useFragment } from '../../../hooks/useFragment';

interface AudiogramSocialExportDialogProps {
  platform: SocialPlatform;
  show: boolean;
  variant: string;
  onHide: () => void;
  onSubmit: (data: any) => Promise<void>;
}

export function AudiogramSocialExportDialog({
  platform,
  show,
  variant,
  onHide,
  onSubmit,
}: AudiogramSocialExportDialogProps) {
  const { fragment } = useFragment();
  const { $t } = useIntl();
  const useExport = useSocialExportFactory(platform);
  const { PublishForm: ExportForm } = useExport();

  const mediaUrlQuery = useQuery({
    queryKey: ['audiogram-media-url', fragment.id, variant],
    queryFn: () => apiClient.fragments.audiogramMediaUrl(fragment.id, variant),
  });

  return (
    <QueryView query={mediaUrlQuery} noLoader>
      {(data) => {
        return (
          <SocialExportContext.Provider value={{ mediaUrl: data }}>
            <Dialog open={show} onClose={onHide} fullWidth maxWidth="md" scroll="body">
              <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
                {platform.Icon()}{' '}
                <span style={{ marginLeft: 8 }}>
                  <FormattedMessage
                    id="audiogram.exportToPlatform"
                    defaultMessage="Export audiogram to {platformName}"
                    values={{ platformName: platform.name }}
                  />
                </span>
                <IconButton
                  aria-label={$t({
                    id: 'common.close',
                    defaultMessage: 'Cerrar',
                  })}
                  onClick={onHide}
                  sx={{ ml: 'auto' }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent dividers>
                <ExportForm
                  fragment={fragment}
                  videoUrl={data}
                  defaultSettings={undefined}
                  onUpload={async (formData) => {
                    await onSubmit(formData);
                  }}
                  actions={
                    <FormActionsMui
                      separator
                      saveIcon={<UploadIcon />}
                      saveLabel={$t({
                        id: 'socialExport.form.publish',
                        defaultMessage: 'Publicar',
                      })}
                      cancel
                    />
                  }
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={onHide}>
                  <FormattedMessage id="common.close" defaultMessage="Cerrar" />
                </Button>
              </DialogActions>
            </Dialog>
          </SocialExportContext.Provider>
        );
      }}
    </QueryView>
  );
}
